import Dropdown, {
  DropdownItemProps,
} from "components/DesignSystem/Dropdown/Dropdown";
import * as Popover from "@radix-ui/react-popover";
import { useModal } from "hooks/useModal";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import {
  useChangeTransactionStatusMutation,
  useUnlinkInvoicesMutation,
  useGetMappedInvoiceQuery,
  useGetMappedBillQuery,
} from "store/apis/transactions";
import { BackendError } from "types/utils/error";
import { useDispatch } from "react-redux";
import {
  openSplitTransactionModal,
  openEditSplitTransactionModal,
  openDeleteSplitTransactionModal,
} from "store/slices/splitTransaction";
import { Transactions } from "types/Models/books";
import { useState } from "react";
import { EditModal } from "./EditModal";
import { DeleteTransaction } from "./Slider/DeleteTransaction";
import { LinkInkleBillModal } from "components/LinkInkleBillModal/LinkInkleBillModal";
import { LinkInkleInvoiceModal } from "components/LinkInkleInvoiceModal/LinkInkleInvoiceModal";
import { ChangeCategory } from "./UnLinkTransaction";

type BuildTransactionActionsParams = {
  transactionId: string;
  transactionData?: Transactions;
  onRequestInfo?: ({ transactionId }: { transactionId: string }) => void;
  sendToChat?: (uuid: string) => void;
  onRowClick?: (uuid: string, e: Event) => void;
  onDuplicate?: (transaction: any) => void;
};

export const useTransactionActions = ({
  transactionId,
  transactionData,
  onRequestInfo,
  sendToChat,
  onRowClick,
  onDuplicate,
}: BuildTransactionActionsParams) => {
  const dispatch = useDispatch();
  const entityId = useCurrentEntityId();
  const group = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();
  const editModal = useModal();
  const deleteModal = useModal();
  const linkInkleInvoiceModal = useModal();
  const linkInkleBillModal = useModal();
  const changeCategoryModal = useModal();
  const [pendingAction, setPendingAction] = useState<
    "split" | "link" | "link-bill" | null
  >(null);

  const [changeTransactionStatus] = useChangeTransactionStatusMutation();
  const [unlinkInvoices] = useUnlinkInvoicesMutation();

  const categoryName = transactionData?.transaction?.category?.name || "";
  const hasLinkedInvoice = transactionData?.invoices?.some(
    ({ has_inkle_invoice }) => has_inkle_invoice
  );
  const hasLinkedBill = transactionData?.invoices?.some(
    ({ has_inkle_bill }) => has_inkle_bill
  );

  const { data: linkedInvoices } = useGetMappedInvoiceQuery(
    {
      transactionId,
      entityId,
    },
    {
      skip: !hasLinkedInvoice,
    }
  );

  const { data: linkedBills } = useGetMappedBillQuery(
    {
      transactionId,
      entityId,
    },
    {
      skip: !hasLinkedBill,
    }
  );

  const changeStatus = async ({
    transactionStatus,
  }: {
    transactionStatus: "EXCLUDED" | "RESTORED";
  }) => {
    try {
      await changeTransactionStatus({
        entityId,
        payload: {
          transaction_ids: [transactionId],
          transaction_status: transactionStatus,
        },
      }).unwrap();

      const message =
        transactionStatus === "EXCLUDED"
          ? "Transaction excluded"
          : "Transaction restored";

      successToast({
        message,
      });
    } catch (error) {
      alertToast(
        { message: (error as BackendError)?.data?.error?.message },
        error as {}
      );
    }
  };

  const confirmUnlinkInvoices = async () => {
    try {
      await unlinkInvoices({
        entityId: entityId,
        financial_record_type:
          linkedInvoices?.invoices?.at(0)?.financial_record_type!,
        transactionId,
        groupId: group.uuid,
      }).unwrap();
      successToast({
        message: "Invoices unlinked successfully",
      });
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  const confirmUnlinkBills = async () => {
    try {
      await unlinkInvoices({
        entityId: entityId,
        financial_record_type:
          linkedBills?.invoices?.at(0)?.financial_record_type!,
        transactionId,
        groupId: group.uuid,
      }).unwrap();
      successToast({
        message: "Bills unlinked successfully",
      });
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  const onConfirmSplitTransaction = () => {
    setPendingAction("split");
    changeCategoryModal.open();
  };

  const onConfirmLinkInvoice = () => {
    setPendingAction("link");
    changeCategoryModal.open();
  };

  const onConfirmLinkBill = () => {
    setPendingAction("link-bill");
    changeCategoryModal.open();
  };

  const handleConfirmChangeCategory = () => {
    changeCategoryModal.close();
    if (pendingAction === "split" && transactionData) {
      dispatch(openSplitTransactionModal(transactionData));
    } else if (pendingAction === "link") {
      linkInkleInvoiceModal.open();
    } else if (pendingAction === "link-bill") {
      linkInkleBillModal.open();
    }
    setPendingAction(null);
  };

  const Action = {
    Edit: (props: DropdownItemProps) => (
      <Dropdown.Item {...props} onSelect={editModal?.open}>
        Edit transaction
      </Dropdown.Item>
    ),

    Delete: (props: DropdownItemProps) => (
      <Dropdown.Item {...props} type="danger" onSelect={deleteModal?.open}>
        Delete transaction
      </Dropdown.Item>
    ),

    SplitTransaction: (props: DropdownItemProps) => (
      <Dropdown.Item
        {...props}
        onSelect={() => {
          const shouldShowWarning =
            categoryName === "Bank Transfer" ||
            categoryName === "Pay Down Credit";

          if (shouldShowWarning) {
            onConfirmSplitTransaction();
          } else {
            transactionData &&
              dispatch(openSplitTransactionModal(transactionData));
          }
        }}
      >
        Split transaction
      </Dropdown.Item>
    ),

    EditSplitTransaction: (props: DropdownItemProps) => (
      <Dropdown.Item
        {...props}
        onSelect={() =>
          transactionData &&
          dispatch(openEditSplitTransactionModal(transactionData))
        }
      >
        Edit split transaction
      </Dropdown.Item>
    ),

    DeleteSplitTransaction: (props: DropdownItemProps) => (
      <Dropdown.Item
        {...props}
        onSelect={() =>
          transactionData &&
          dispatch(openDeleteSplitTransactionModal(transactionData))
        }
      >
        Delete split transaction
      </Dropdown.Item>
    ),

    ExcludeTransaction: (props: DropdownItemProps) => (
      <Dropdown.Item
        {...props}
        onSelect={() => changeStatus({ transactionStatus: "EXCLUDED" })}
      >
        Exclude transaction
      </Dropdown.Item>
    ),

    RestoreTransaction: (props: DropdownItemProps) => (
      <Dropdown.Item
        {...props}
        onSelect={() => changeStatus({ transactionStatus: "RESTORED" })}
      >
        Restore transaction
      </Dropdown.Item>
    ),

    LinkInkleInvoice: (props: DropdownItemProps) => (
      <Dropdown.Item
        {...props}
        onSelect={(e) => {
          e.stopPropagation();
          const shouldShowWarning =
            categoryName === "Bank Transfer" ||
            categoryName === "Pay Down Credit";

          if (shouldShowWarning) {
            onConfirmLinkInvoice();
          } else {
            linkInkleInvoiceModal.open();
          }
        }}
      >
        Link Inkle invoice
      </Dropdown.Item>
    ),

    UnlinkInkleInvoice: (props: DropdownItemProps) => (
      <Dropdown.Item
        {...props}
        onSelect={(e) => {
          e.stopPropagation();
          confirmUnlinkInvoices();
        }}
      >
        Unlink Inkle invoice(s)
      </Dropdown.Item>
    ),

    LinkInkleBill: (props: DropdownItemProps) => (
      <Dropdown.Item
        {...props}
        onSelect={(e) => {
          e.stopPropagation();
          const shouldShowWarning =
            categoryName === "Bank Transfer" ||
            categoryName === "Pay Down Credit";

          if (shouldShowWarning) {
            onConfirmLinkBill();
          } else {
            linkInkleBillModal.open();
          }
        }}
      >
        Link Inkle bill
      </Dropdown.Item>
    ),

    UnlinkInkleBill: (props: DropdownItemProps) => (
      <Dropdown.Item
        {...props}
        onSelect={(e) => {
          e.stopPropagation();
          confirmUnlinkBills();
        }}
      >
        Unlink Inkle bill(s)
      </Dropdown.Item>
    ),

    AddComment: (props: DropdownItemProps) => (
      <Popover.Trigger asChild>
        <Dropdown.Item {...props}>Add comment</Dropdown.Item>
      </Popover.Trigger>
    ),
    ...(onRequestInfo
      ? {
          RequestInfo: (props: DropdownItemProps) => (
            <Dropdown.Item
              {...props}
              onSelect={() => onRequestInfo({ transactionId })}
            >
              Request info
            </Dropdown.Item>
          ),
        }
      : {}),

    ...(sendToChat
      ? {
          SendToChat: (props: DropdownItemProps) => (
            <Dropdown.Item
              {...props}
              onSelect={() => sendToChat(transactionId)}
            >
              Send to chat
            </Dropdown.Item>
          ),
        }
      : {}),

    ...(onRowClick
      ? {
          AddInvoice: (props: DropdownItemProps) => (
            <Dropdown.Item
              {...props}
              onSelect={() => {
                onRowClick(transactionId, new Event("click"));
              }}
            >
              Add invoice
            </Dropdown.Item>
          ),
        }
      : {}),

    ...(onDuplicate
      ? {
          Duplicate: (props: DropdownItemProps) => (
            <Dropdown.Item
              {...props}
              onSelect={() =>
                transactionData && onDuplicate(transactionData.transaction)
              }
            >
              Duplicate transaction
            </Dropdown.Item>
          ),
        }
      : {}),
  };

  const modals = (
    <>
      {editModal.isOpen && (
        <EditModal
          transactionId={transactionId}
          isOpen={editModal.isOpen}
          close={editModal.close}
        />
      )}
      {deleteModal.isOpen && (
        <DeleteTransaction
          transactionId={transactionId}
          close={deleteModal.close}
          isOpen={deleteModal.isOpen}
        />
      )}
      {linkInkleInvoiceModal.isOpen && (
        <LinkInkleInvoiceModal
          transactionId={transactionId}
          isOpen={linkInkleInvoiceModal.isOpen}
          close={linkInkleInvoiceModal.close}
        />
      )}
      {linkInkleBillModal.isOpen && (
        <LinkInkleBillModal
          transactionId={transactionId}
          isOpen={linkInkleBillModal.isOpen}
          close={linkInkleBillModal.close}
        />
      )}
      <ChangeCategory
        isOpen={changeCategoryModal.isOpen}
        close={changeCategoryModal.close}
        onConfirm={handleConfirmChangeCategory}
        categoryName={categoryName}
      />
    </>
  );

  return { Action, modals };
};
